import { differenceInCalendarDays, differenceInDays, format as formatFns, isValid, parse, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

/*
 * Dates, sur date-fns, avec la sémantique de moment que l'application utilisait.
 *
 * Deux points comptent pour que l'affichage ne bouge pas :
 * - une date ISO sans heure (`2024-03-01`) est lue en **heure locale**, comme moment, et non en
 *   UTC comme `new Date('2024-03-01')` — sinon la veille s'affiche à l'ouest de Greenwich ;
 * - `L` vaut `dd/MM/yyyy`, le format court de la locale française de moment.
 */

export type DateInput = Date | string | number | null | undefined;

/** Format court français (`L` sous moment). */
export const DATE_FR = 'dd/MM/yyyy';
/** Format des dates échangées avec le Pass et des `<input type="date">`. */
export const DATE_ISO = 'yyyy-MM-dd';

/**
 * Convertit une valeur en `Date`.
 *
 * `pattern` est un motif date-fns (`dd/MM/yyyy`…) pour une chaîne qui n'est pas en ISO. Sans
 * motif, une chaîne ISO est lue par `parseISO`, toute autre par le constructeur `Date`.
 *
 * Une valeur absente (`undefined`, `null`, `''`) donne une date **invalide**, jamais l'instant
 * présent (contrairement à `moment(undefined)`) : les fiches API ramènent une date `null` à
 * `undefined`, et une échéance ou une naissance inconnue ne doit pas devenir « aujourd'hui ».
 * Pour l'instant présent, `new Date()`.
 */
export const toDate = (value: DateInput, pattern?: string): Date => {
    if (value === undefined || value === null || value === '') return new Date(NaN);
    if (value instanceof Date) return new Date(value.getTime());
    if (typeof value === 'number') return new Date(value);
    if (pattern) return parse(value, pattern, new Date());

    const iso = parseISO(value);
    return isValid(iso) ? iso : new Date(value);
};

export const isValidDate = (value: DateInput, pattern?: string): boolean => isValid(toDate(value, pattern));

/** Formate une date (`dd/MM/yyyy` par défaut), en français. Date absente ou invalide → `''`, rien à afficher. */
export const formatDate = (value: DateInput, pattern: string = DATE_FR): string => {
    const date = toDate(value);
    return isValid(date) ? formatFns(date, pattern, { locale: fr }) : '';
};

/** Date du jour au format ISO (`yyyy-MM-dd`). */
export const todayIso = (): string => formatFns(new Date(), DATE_ISO);

/**
 * Nombre de jours pleins de `b` à `a` (`a - b`), tronqué vers zéro comme `moment(a).diff(b, 'days')`.
 * Date absente ou invalide → `NaN` : toute comparaison est fausse.
 */
export const diffInDays = (a: DateInput, b: DateInput): number => differenceInDays(toDate(a), toDate(b));

/**
 * Nombre de jours calendaires de `b` à `a` (`a - b`), de minuit à minuit : l'heure est ignorée.
 * C'est le décompte du Pass pour les seuils de résiliation (échéance, renonciation…) ; `diffInDays`
 * en ôterait un jour dès que l'heure courante dépasse celle de `b`. Date absente ou invalide → `NaN`.
 */
export const diffInCalendarDays = (a: DateInput, b: DateInput): number => differenceInCalendarDays(toDate(a), toDate(b));
